import React, { useState } from 'react';
import {
  getEventRequestReceivedEmail,
  getEventApprovedPaymentLinkEmail,
  getEventPaymentSuccessTicketEmail,
  getStoreOrderConfirmationEmail,
  getStoreCargoTrackingEmail,
  getPasswordResetEmail,
  getWelcomeRegisterEmail,
  getProfileUpdateInfoEmail,
  getEventCanceledEmail,
} from '../../../utils/emailTemplates';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

// ─── Sample dummy data for preview ───────────────────────────────────────────
const DUMMY = {
  participantName: 'Ozan Yılmaz',
  eventTitle: 'SUP Yoga ve Beach Games',
  eventDate: '15 Ağustos 2026, Cumartesi – 09:00',
  eventLocation: 'Çeşme, İzmir',
  paymentLink: 'https://www.paynkolay.com/pay/sample-link-preview',
  amount: '1.500 ₺',
  ticketCode: 'PT-8F12A3B0',
  customerName: 'Ozan Yılmaz',
  orderNumber: 'PT-20260801-0042',
  orderItems: [
    { name: 'SUP Paddle Board (Şişme)', quantity: 1, price: '1.200 ₺' },
    { name: 'Can Yeleği (M)', quantity: 1, price: '300 ₺' },
  ],
  totalAmount: '1.500 ₺',
  shippingAddress: 'Atatürk Cad. No:12, Çeşme / İzmir',
  cargoCompany: 'Yurtiçi Kargo',
  trackingNumber: 'YK-9876543210',
  trackingUrl: 'https://www.yurticikargo.com/tr/online-islemler/gonderi-sorgula',
  estimatedDelivery: '17 Ağustos 2026, Pazartesi',
  userName: 'Ozan Yılmaz',
  resetLink: 'https://paddleturkiye.com/password-reset?token=sample-preview-token',
  updateDate: '31 Temmuz 2026, 20:40',
  updateType: 'Profil Fotoğrafı & İletişim Bilgileri',
};

// ─── Template catalog ─────────────────────────────────────────────────────────
const templates = [
  {
    id: 'welcome-register',
    name: '🎉 Aramıza Hoş Geldiniz',
    generate: () => getWelcomeRegisterEmail({ userName: DUMMY?.userName }),
  },
  {
    id: 'password-reset',
    name: '🔐 Şifre Sıfırlama Talebi',
    generate: () => getPasswordResetEmail({ userName: DUMMY?.userName, resetLink: DUMMY?.resetLink }),
  },
  {
    id: 'profile-update',
    name: '🔔 Profil Bilgileri Güncellendi',
    generate: () => getProfileUpdateInfoEmail({ userName: DUMMY?.userName, updateDate: DUMMY?.updateDate, updateType: DUMMY?.updateType }),
  },
  {
    id: 'store-order',
    name: '📦 Siparişiniz Alındı',
    generate: () => getStoreOrderConfirmationEmail({
      customerName: DUMMY?.customerName,
      orderNumber: DUMMY?.orderNumber,
      orderItems: DUMMY?.orderItems,
      totalAmount: DUMMY?.totalAmount,
      shippingAddress: DUMMY?.shippingAddress,
    }),
  },
  {
    id: 'store-cargo',
    name: '🚚 Siparişiniz Kargoya Verildi',
    generate: () => getStoreCargoTrackingEmail({
      customerName: DUMMY?.customerName,
      orderNumber: DUMMY?.orderNumber,
      cargoCompany: DUMMY?.cargoCompany,
      trackingNumber: DUMMY?.trackingNumber,
      trackingUrl: DUMMY?.trackingUrl,
      estimatedDelivery: DUMMY?.estimatedDelivery,
    }),
  },
  {
    id: 'event-request',
    name: '📋 Rezervasyon Talebi Alındı',
    generate: () => getEventRequestReceivedEmail({
      participantName: DUMMY?.participantName,
      eventTitle: DUMMY?.eventTitle,
      eventDate: DUMMY?.eventDate,
      eventLocation: DUMMY?.eventLocation,
    }),
  },
  {
    id: 'event-approved',
    name: '✅ Rezervasyon Onayı & Ödeme Linki',
    generate: () => getEventApprovedPaymentLinkEmail({
      participantName: DUMMY?.participantName,
      eventTitle: DUMMY?.eventTitle,
      eventDate: DUMMY?.eventDate,
      eventLocation: DUMMY?.eventLocation,
      paymentLink: DUMMY?.paymentLink,
      amount: DUMMY?.amount,
    }),
  },
  {
    id: 'event-ticket',
    name: '🎫 Ödeme Başarılı & Etkinlik Bileti',
    generate: () => getEventPaymentSuccessTicketEmail({
      participantName: DUMMY?.participantName,
      eventTitle: DUMMY?.eventTitle,
      eventDate: DUMMY?.eventDate,
      eventLocation: DUMMY?.eventLocation,
      ticketCode: DUMMY?.ticketCode,
      amount: DUMMY?.amount,
    }),
  },
  {
    id: 'event-canceled',
    name: '❌ Rezervasyon İptal Edildi',
    generate: () => getEventCanceledEmail({
      participantName: DUMMY?.participantName,
      eventName: DUMMY?.eventTitle,
      eventDate: DUMMY?.eventDate,
      eventLocation: DUMMY?.eventLocation,
      cancellationReason: 'Etkinlik organizatörü tarafından iptal edilmiştir.',
    }),
  },
];

const EmailTemplateManager = () => {
  const [selectedTemplate, setSelectedTemplate] = useState(templates?.[0]?.id);
  const [testEmailStatus, setTestEmailStatus] = useState('idle');
  const { user } = useAuth();

  const activeTemplate = templates?.find(t => t?.id === selectedTemplate);

  const renderPreview = () => {
    if (!activeTemplate) return null;
    const html = activeTemplate?.generate();
    return (
      <iframe
        key={selectedTemplate}
        srcDoc={html}
        title={`${activeTemplate?.name} Önizleme`}
        className="w-full border-0 block"
        style={{ height: '680px', minHeight: '600px' }}
        sandbox="allow-same-origin"
      />
    );
  };

  const handleSendTestEmail = async () => {
    const adminEmail = user?.email;
    if (!adminEmail) {
      setTestEmailStatus('error');
      setTimeout(() => setTestEmailStatus('idle'), 3000);
      return;
    }
    setTestEmailStatus('sending');
    try {
      const html = activeTemplate?.generate();
      const { error } = await supabase?.functions?.invoke('send-email', {
        body: {
          to: adminEmail,
          subject: `[TEST] ${activeTemplate?.name} — PaddleTürkiye Şablon Önizlemesi`,
          type: 'template_preview',
          data: {
            htmlContent: html,
            templateLabel: activeTemplate?.name,
          },
        },
      });
      if (error) throw error;
      setTestEmailStatus('sent');
      setTimeout(() => setTestEmailStatus('idle'), 4000);
    } catch (err) {
      console.error('Test email error:', err);
      setTestEmailStatus('error');
      setTimeout(() => setTestEmailStatus('idle'), 3000);
    }
  };

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">E-posta Şablonları</h2>
        <p className="text-sm text-gray-500 mt-1">
          Tüm sistem e-postalarının canlı önizlemesi — PaddleTürkiye Emerald marka kimliği
        </p>
      </div>
      {/* Two-Column Layout */}
      <div className="flex flex-col lg:flex-row gap-6 items-start w-full">

        {/* LEFT SIDEBAR: Template List */}
        <div className="w-full lg:w-1/3 flex flex-col gap-2 bg-gray-50 p-4 rounded-lg border border-gray-200">
          <h3 className="font-bold text-gray-800 mb-2 border-b pb-2">E-posta Şablonları</h3>
          {templates?.map((template) => (
            <button
              key={template?.id}
              type="button"
              onClick={() => { setSelectedTemplate(template?.id); setTestEmailStatus('idle'); }}
              className={`text-left px-4 py-3 rounded-md border transition-all ${
                selectedTemplate === template?.id
                  ? 'bg-emerald-600 text-white border-emerald-700 shadow-sm font-medium'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-emerald-300 hover:bg-emerald-50'
              }`}
            >
              {template?.name}
            </button>
          ))}

          {/* Test Send Button */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              disabled={testEmailStatus === 'sending'}
              onClick={handleSendTestEmail}
              className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold transition-all duration-200 ${
                testEmailStatus === 'sending' ?'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                  : testEmailStatus === 'sent' ?'bg-emerald-100 text-emerald-700 border border-emerald-300 hover:bg-emerald-200'
                  : testEmailStatus === 'error' ?'bg-red-100 text-red-700 border border-red-300 hover:bg-red-200' :'bg-emerald-600 text-white border border-emerald-700 hover:bg-emerald-700 active:scale-95'
              }`}
            >
              {testEmailStatus === 'sending' && (
                <>
                  <svg className="animate-spin w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  <span>Gönderiliyor...</span>
                </>
              )}
              {testEmailStatus === 'sent' && <><span>✅</span><span>Test Gönderildi!</span></>}
              {testEmailStatus === 'error' && <><span>❌</span><span>Gönderilemedi</span></>}
              {testEmailStatus === 'idle' && <><span>📨</span><span>Test Gönder</span></>}
            </button>
            {testEmailStatus === 'idle' && user?.email && (
              <p className="text-xs text-center text-gray-500 mt-1.5 truncate px-1">→ {user?.email}</p>
            )}
            {testEmailStatus === 'sent' && (
              <p className="text-xs text-center text-emerald-600 mt-1.5 font-medium">Admin gelen kutunuzu kontrol edin</p>
            )}
            {testEmailStatus === 'error' && (
              <p className="text-xs text-center text-red-500 mt-1.5">RESEND_API_KEY ayarını kontrol edin</p>
            )}
          </div>
        </div>

        {/* RIGHT MAIN AREA: Live Preview */}
        <div className="w-full lg:w-2/3 bg-white p-0 rounded-lg border border-gray-200 shadow-sm relative z-0 isolate overflow-hidden">
          <div className="bg-gray-100 px-4 py-2 border-b text-sm font-semibold text-gray-600 flex justify-between items-center">
            <span>Canlı Önizleme — {activeTemplate?.name}</span>
          </div>
          <div className="p-4 w-full overflow-x-auto bg-gray-100">
            {renderPreview()}
          </div>
        </div>

      </div>
    </div>
  );
};

export default EmailTemplateManager;
